import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress,
  InputLabel,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import PrimaryButton from './ui/PrimaryButton';
import axios from 'axios';
import { authService } from '../services/authService';

import { API_BASE_URL } from '../config';

const LoginModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');

  useEffect(() => {
    if (open) {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
      let result = '';
      for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
      setCaptchaText(result);
    }
    if (open && authService.isRememberMeEnabled()) {
      const savedUsername = authService.getSavedUsername();
      if (savedUsername) setUsername(savedUsername);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (captchaInput.trim().toLowerCase() !== captchaText.trim().toLowerCase()) {
      setLoading(false);
      setError('کد امنیتی صحیح نیست');
      setCaptchaInput('');
      // regenerate
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
      let result = '';
      for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
      setCaptchaText(result);
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, { username, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      authService.setRememberMe(username);
      
      // Show welcome message
      const fullName = user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username;
      setWelcomeMessage(`خوش آمدید ${fullName}! 🎉`);
      
      onClose?.();
      if (user.role === 'admin') navigate('/reports'); else navigate('/display');
    } catch (err) {
      setError(err.response?.status === 401 ? 'نام کاربری یا رمز عبور اشتباه است' : 'خطا در اتصال به سرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #ffffff 100%)',
          border: '1px solid rgba(50, 51, 56, 0.15)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ height: 6, background: 'linear-gradient(90deg, #1a335b, #b79961)' }} />
      <DialogTitle sx={{ pr: 6, fontWeight: 800, color: 'text.primary' }}>
        ورود به ERP
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          {/* Visual panel */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              background: 'radial-gradient(circle at 20% 20%, #1a335b22 0, #f7f8fa 60%)',
              borderRight: { sm: '1px solid rgba(50, 51, 56, 0.12)' }
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '24px',
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #1a335b, #b79961)',
                boxShadow: '0 10px 30px rgba(50, 51, 56,0.3)'
              }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>به ERP خوش آمدید</Typography>
              <Typography variant="body2" color="text.secondary">برای ادامه وارد حساب خود شوید</Typography>
            </Box>
          </Box>

          {/* Form panel */}
          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1.2, p: { xs: 3, sm: 4 } }}>
          <TextField
            fullWidth
            label="نام کاربری"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <TextField
            fullWidth
            label="رمز عبور"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LoginIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* Captcha */}
          <Box sx={{ mt: 1 }}>
            <InputLabel sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon fontSize="small" /> کد امنیتی
            </InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, p: 2, bgcolor: '#f7f8fa', borderRadius: 2, fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '2px', userSelect: 'none', textAlign: 'center', border: '1px dashed #d0d4dc' }}>{captchaText}</Box>
              <IconButton aria-label="refresh captcha" onClick={() => {
                const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
                let result = '';
                for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
                setCaptchaText(result);
              }} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              placeholder="کد نمایش داده‌شده را وارد کنید"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SecurityIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          <PrimaryButton 
            type="submit" 
            fullWidth 
            disabled={loading || !username || !password || !captchaInput} 
            sx={{ 
              mt: 3,
              py: 1.5,
              background: 'linear-gradient(90deg, #b79961, #a68955)',
              ':hover': { background: 'linear-gradient(90deg, #a68955, #b79961)' }
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'ورود'}
          </PrimaryButton>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            با ورود شما شرایط استفاده از سامانه ERP را می‌پذیرید
          </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      {/* Welcome Message Snackbar */}
      <Snackbar
        open={!!welcomeMessage}
        autoHideDuration={4000}
        onClose={() => setWelcomeMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          onClose={() => setWelcomeMessage('')} 
          severity="success" 
          sx={{ 
            width: '100%',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            borderRadius: 2
          }}
        >
          {welcomeMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default LoginModal;


